import {
  type AreaComponent,
  flattenPolygonRing,
  GEOMETRY_TOLERANCE,
  isSimplePolygon,
  MAX_SHAPE_HOLE_POINTS,
  MAX_SHAPE_POINTS,
  MIN_SHAPE_POINTS,
  type PlacementPosition,
  polygonArea,
  type ShapePoint,
  type ShapeVertex,
  shapeBoundsOf,
} from '@repo/api-contracts';

const toXy = (points: readonly PlacementPosition[]): { x: number; y: number }[] =>
  points.map((point) => ({ x: point.xMm, y: point.yMm }));

/** 그리는 고리 — 인쇄 영역 바깥 고리(점 최대 256개)와 인쇄 제외 영역(구멍, 점 최대 64개). */
export type ShapeRingKind = 'area' | 'hole';

/** 고리 종류별 점 한도와 그 문구. */
export const shapeRingLimitOf = (
  kind: ShapeRingKind,
): { readonly maxPoints: number; readonly message: string } =>
  kind === 'hole'
    ? {
        maxPoints: MAX_SHAPE_HOLE_POINTS,
        message: `인쇄 제외 영역 점은 최대 ${MAX_SHAPE_HOLE_POINTS}개입니다.`,
      }
    : { maxPoints: MAX_SHAPE_POINTS, message: `인쇄 영역 점은 최대 ${MAX_SHAPE_POINTS}개입니다.` };

/** 닫으려는 인쇄 영역(또는 인쇄 제외 영역) 점열(표면 mm)을 받을 수 없는 이유. 받을 수 있으면 null. */
export const validateShapeRing = (
  points: readonly ShapeVertex[],
  kind: ShapeRingKind = 'area',
): string | null => {
  if (points.length < MIN_SHAPE_POINTS) return `점을 ${MIN_SHAPE_POINTS}개 이상 찍어 주세요.`;
  const limit = shapeRingLimitOf(kind);
  if (points.length > limit.maxPoints) return limit.message;
  // 곡선 손잡이가 있으면(원형 구멍) 풀어 쓴 고리로 본다. 손잡이가 없으면 점 그대로다.
  const flat = flattenPolygonRing(points);
  const ring = toXy(flat);
  if (polygonArea(ring) <= GEOMETRY_TOLERANCE || !isSimplePolygon(ring))
    return '인쇄 영역 모양이 스스로 교차하거나 넓이가 없습니다. 점을 고친 뒤 다시 닫아 주세요.';
  const bounds = shapeBoundsOf(flat);
  if (bounds.widthMm <= GEOMETRY_TOLERANCE || bounds.heightMm <= GEOMETRY_TOLERANCE)
    return '인쇄 영역이 너무 작습니다.';
  return null;
};

/** 허용오차(1e-6)보다 작아 경계 판정을 바꾸지 않고 부동소수 찌꺼기만 지운다. */
const DRAWN_SHAPE_PRECISION = 1e7;
export const roundMm = (value: number): number =>
  Math.round(value * DRAWN_SHAPE_PRECISION) / DRAWN_SHAPE_PRECISION + 0;

/**
 * 표면 mm 점열 → 외접 사각형 왼쪽 위를 원점으로 하는 다각형 컴포넌트와 배치 위치(왼쪽 위).
 * 가로·세로는 반올림한 점의 외접 크기라 계약의 외접 일치 검사를 통과한다.
 */
export const drawnComponentOf = (
  points: readonly PlacementPosition[],
  base: { id: string; name: string },
): { component: AreaComponent; position: PlacementPosition } => {
  const { left, top } = shapeBoundsOf(points);
  const local = points.map((point) => ({
    xMm: roundMm(point.xMm - left),
    yMm: roundMm(point.yMm - top),
  }));
  // 반올림한 점의 외접 크기도 다시 반올림한다 — 최대 − 최소 뺄셈이 남기는 찌꺼기를 지운다.
  const bounds = shapeBoundsOf(local);
  const widthMm = roundMm(bounds.widthMm);
  const heightMm = roundMm(bounds.heightMm);

  return {
    component: {
      id: base.id,
      name: base.name,
      widthMm,
      heightMm,
      shape: { type: 'polygon', points: local },
    },
    position: { xMm: roundMm(left), yMm: roundMm(top) },
  };
};

const roundPoint = (point: ShapePoint): ShapePoint => ({
  xMm: roundMm(point.xMm),
  yMm: roundMm(point.yMm),
});

/** 점과 곡선 손잡이를 dx·dy 만큼 옮기고 부동소수 찌꺼기를 지운다(손잡이가 없으면 만들지 않는다). */
export const shiftVertices = (
  points: readonly ShapeVertex[],
  dxMm: number,
  dyMm: number,
): ShapeVertex[] => {
  const shift = (point: ShapePoint): ShapePoint =>
    roundPoint({ xMm: point.xMm + dxMm, yMm: point.yMm + dyMm });
  return points.map((point) => ({
    ...shift(point),
    ...(point.handleIn === undefined ? {} : { handleIn: shift(point.handleIn) }),
    ...(point.handleOut === undefined ? {} : { handleOut: shift(point.handleOut) }),
  }));
};

/** 표면 mm 구멍 점열(곡선 손잡이 포함) → 컴포넌트 mm(배치 위치를 뺀 값). 부동소수 찌꺼기는 지운다. */
export const holeRingOf = (
  points: readonly ShapeVertex[],
  position: PlacementPosition,
): ShapeVertex[] => shiftVertices(points, -position.xMm, -position.yMm);
